import React, { useEffect, useRef, useState } from 'react';
import { StarRating } from '../profile/customersurveyform';
import { useRecruitingStore } from '../../state/recruitingStore';
import useOtherUsersStore from '../../state/otherUsersStore';
import { useModalStore } from '../../state/modalStore';
import { useMatrixStore } from '../../state/matrixStore';
import { useMessagingStore } from '../../state/messagingStore';
import { useProfileStore } from '../../state/profileStore';
import { useConversationStore } from '../../state/conversationStore';
import { Rnd } from 'react-rnd';
import AllReviewsList from '../customers/AllReviewsList';


// DEV: use REACT_APP_PUBLIC_SITE_ORIGIN (default http://localhost:3020)
// PROD: use REACT_APP_PUBLIC_SITE_ORIGIN if set, else https://www.ehandshake.com
const PUBLIC_SITE =
  process.env.REACT_APP_PUBLIC_SITE_ORIGIN
    ? process.env.REACT_APP_PUBLIC_SITE_ORIGIN
    : (process.env.NODE_ENV === 'development'
        ? 'http://localhost:3020'
        : 'https://www.ehandshake.com');

function buildServiceOverlapMapUrl(overlap) {
  if (!overlap) return null;

  const viewerLat = Number(overlap.viewer_lat);
  const viewerLng = Number(overlap.viewer_lng);
  const candidateLat = Number(overlap.candidate_lat);
  const candidateLng = Number(overlap.candidate_lng);

  if (
    !Number.isFinite(viewerLat) ||
    !Number.isFinite(viewerLng) ||
    !Number.isFinite(candidateLat) ||
    !Number.isFinite(candidateLng)
  ) {
    return null;
  }

  const viewerRadiusMiles = Number(overlap.viewer_radius_miles);
  const candidateRadiusMiles = Number(overlap.candidate_radius_miles);

  if (!Number.isFinite(viewerRadiusMiles) || !Number.isFinite(candidateRadiusMiles)) {
    return null;
  }

  const token = process.env.REACT_APP_MAPBOX_TOKEN;
  if (!token) return null;

  // --- helpers ---
  const milesToMeters = (m) => m * 1609.344;

  // Approximate a circle polygon (GeoJSON) around a point.
  // Uses a simple spherical approximation (good enough for a small static preview).
  function circlePolygon(lng, lat, radiusMeters, steps = 64) {
    const coords = [];
    const earthRadius = 6378137; // meters
    const latRad = (lat * Math.PI) / 180;
    const lngRad = (lng * Math.PI) / 180;
    const angular = radiusMeters / earthRadius;

    for (let i = 0; i <= steps; i++) {
      const bearing = (i / steps) * 2 * Math.PI;

      const sinLat = Math.sin(latRad);
      const cosLat = Math.cos(latRad);
      const sinAng = Math.sin(angular);
      const cosAng = Math.cos(angular);

      const lat2 = Math.asin(sinLat * cosAng + cosLat * sinAng * Math.cos(bearing));
      const lng2 =
        lngRad +
        Math.atan2(
          Math.sin(bearing) * sinAng * cosLat,
          cosAng - sinLat * Math.sin(lat2)
        );

      coords.push([(lng2 * 180) / Math.PI, (lat2 * 180) / Math.PI]);
    }

    return coords;
  }

  const viewerRadiusMeters = milesToMeters(viewerRadiusMiles);
  const candidateRadiusMeters = milesToMeters(candidateRadiusMiles);

  // Build GeoJSON FeatureCollection with two polygons
  const geojson = {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: {
          // style hints for Mapbox static geojson overlay
          // fill + outline
          'fill': '#00aef7',
          'fill-opacity': 0.18,
          'stroke': '#00aef7',
          'stroke-width': 2,
          'stroke-opacity': 0.9
        },
        geometry: {
          type: 'Polygon',
          coordinates: [circlePolygon(viewerLng, viewerLat, viewerRadiusMeters)]
        }
      },
      {
        type: 'Feature',
        properties: {
          'fill': '#f5c542',
          'fill-opacity': 0.18,
          'stroke': '#f5c542',
          'stroke-width': 2,
          'stroke-opacity': 0.9
        },
        geometry: {
          type: 'Polygon',
          coordinates: [circlePolygon(candidateLng, candidateLat, candidateRadiusMeters)]
        }
      }
    ]
  };

  const encoded = encodeURIComponent(JSON.stringify(geojson));

  // Mapbox Static Images API supports `geojson(<encoded>)` overlays
  const url =
    `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/` +
    `geojson(${encoded})/auto/300x200?access_token=${token}`;

  return url;
}



export default function CandidateListModal({
  opened = false,
  onClose,
  candidateName = 'Users Information',
  top = 0,
  right = 0,
  offsetRight = 0,
  candidate = null,
  viewerUId = null,
}) {
  console.log(
    'MAPBOX TOKEN:',
    process.env.REACT_APP_MAPBOX_TOKEN
  );
  const myUid = Number(viewerUId) || Number(window.localStorage.getItem('u_id')) || null;
  const candidateId = Number((candidate && (candidate.u_id ?? candidate.id)) ?? NaN);
  const isSelf = Number.isFinite(myUid) && Number.isFinite(candidateId) && Number(myUid) === Number(candidateId);
  const openWithCandidate = useMatrixStore(s => s.openWithCandidate);
  const openChatCenter = useMatrixStore(s => s.open);        // NEW: actually open ChatCenter
  const candidatePresence = useMatrixStore(s =>
    Number.isFinite(candidateId) ? s.getUserPresence(`@u${candidateId}:ehandshake.com`) : 'offline'
  );
  const setSelectedThread = useMessagingStore(s => s.setSelectedThread);
  const createLocalInviteThread = useMessagingStore(s => s.createLocalInviteThread);

  const bringToFront = useModalStore(s => s.bringToFront);
  const candidateZ = useModalStore(s => s.getZIndex('candidate'));

  const [visible, setVisible] = useState(false);
  const [analysisOpen, setAnalysisOpen] = useState(false);

  // Analysis hover toast (single shared toast area for all panels)
  // status: 'GREEN' | 'RED' | 'YELLOW' | 'GRAY'
  const [analysisHoverToast, setAnalysisHoverToast] = useState(null);

  // Analysis – Panel 1: Customer Focus match (viewer vs candidate)
  const [viewerCustomerFocus, setViewerCustomerFocus] = useState(null);
  const [viewerDisplayName, setViewerDisplayName] = useState('');
  const [viewerProfessionText, setViewerProfessionText] = useState(null);

  // Analysis – Panel 5: Scores (viewer)
  const [viewerAvgRating, setViewerAvgRating] = useState(null);
  const [viewerReviewCount, setViewerReviewCount] = useState(null);
  // Analysis – Panel 6: Customers (viewer)
  const [viewerCustomerCount, setViewerCustomerCount] = useState(null);

  // Analysis – Panel 6: Customers (candidate) — REAL customers only (exclude "Imported" bucket)
  const [candidateCustomerCount, setCandidateCustomerCount] = useState(null);

  const [isLoadingViewerFocus, setIsLoadingViewerFocus] = useState(false);


  // Analysis – Panel 2: Service Area overlap (viewer vs candidate)
  const [serviceOverlap, setServiceOverlap] = useState(null);
  const [isLoadingServiceOverlap, setIsLoadingServiceOverlap] = useState(false);  

  // Analysis – Panel 3: Profession Exclusivity (viewer vs candidate)
  const [exclusivity, setExclusivity] = useState(null);
  const [isLoadingExclusivity, setIsLoadingExclusivity] = useState(false);


  // Reviews (scroll-only section)
  const [reviews, setReviews] = useState([]);
  const [isLoadingReviews, setIsLoadingReviews] = useState(false);
  const [visibleCount, setVisibleCount] = useState(8); // show 8 initially, then load 8 more
  const isFetchingMoreRef = useRef(false);
  // Panel 4 uses the *fetched* reviews list (not candidate.review_count, which is often missing)
  const reviewCount = Array.isArray(reviews) ? reviews.length : 0;

  useEffect(() => {
    if (!opened) return;
    if (!Number.isFinite(candidateId) || candidateId <= 0) return;

    let isMounted = true;

    const fetchCandidateReviews = async () => {
      try {
        setIsLoadingReviews(true);
        setVisibleCount(8);

        const res = await fetch(
          `/api/profile/${candidateId}/reviews`,
          { method: 'GET', credentials: 'include' }
        );

        if (!res.ok) {
          console.error('[CandidateListModal] Failed to fetch reviews:', res.status);
          return;
        }

        const data = await res.json();
        if (isMounted && Array.isArray(data)) {
          setReviews(data);
        }
      } catch (err) {
        console.error('[CandidateListModal] Review fetch error:', err);
      } finally {
        if (isMounted) setIsLoadingReviews(false);
      }
    };

    fetchCandidateReviews();

    return () => {
      isMounted = false;
    };
  }, [opened, candidateId]);

// Fetch viewer customer focus only when Analysis is opened
useEffect(() => {
  if (!analysisOpen) return;
  if (!Number.isFinite(myUid) || myUid <= 0) return;

  let isMounted = true;

  const fetchViewerFromProfileStore = async () => {
    try {
      setIsLoadingViewerFocus(true);

      // IMPORTANT:
      // profileStore.refreshProfile() fetches /api/profile/:id AND applies its rating-summary patch.
      const refreshProfile = useProfileStore.getState().refreshProfile;
      const data = await refreshProfile(myUid);

      if (!data) return;

      // Viewer name + customer focus (Panel 1)
      const focus =
        data?.u_customer_focus ??
        data?.user?.u_customer_focus ??
        data?.profile?.u_customer_focus ??
        null;

      const first =
        data?.u_first_name ??
        data?.user?.u_first_name ??
        data?.profile?.u_first_name ??
        '';

      const last =
        data?.u_last_name ??
        data?.user?.u_last_name ??
        data?.profile?.u_last_name ??
        '';

      const fullName = [first, last].filter(Boolean).join(' ').trim();

      const viewerProfession =
        data?.b_industry ??
        data?.business?.b_industry ??
        data?.b_business_industry ??
        data?.business?.b_business_industry ??
        data?.profile?.b_industry ??
        data?.profile?.b_business_industry ??
        null;

      // Viewer rating/count (Panel 5) — now sourced from the refreshed profileStore payload
      const vCountRaw = data?.u_review_count ?? null;
      const vAvgRaw = data?.u_avg_rating ?? null;

      const vCount = (vCountRaw === null || vCountRaw === undefined) ? null : Number(vCountRaw);
      const vAvg = (vAvgRaw === null || vAvgRaw === undefined) ? null : Number(vAvgRaw);

      // Viewer customers count (Panel 6) — sourced from refreshed profileStore payload
// Viewer customers count (Panel 6) — sourced from refreshed profileStore payload
      // NOTE: refreshProfile() may not *return* the customer count, even though it updates the store.
      // So we also check the profileStore snapshot as a fallback.
      const ps = useProfileStore.getState();
      const psProfile =
        ps?.profile ??
        ps?.profileData ??
        ps?.profileUser ??
        ps?.user ??
        ps?.me ??
        null;

      const psCustRaw =
        psProfile?.u_customer_count ??
        psProfile?.customer_count ??
        psProfile?.customers_count ??
        psProfile?.u_customers_count ??
        (Array.isArray(psProfile?.customers) ? psProfile.customers.length : null) ??
        (Array.isArray(psProfile?.customerList) ? psProfile.customerList.length : null) ??
        (Array.isArray(psProfile?.customer_list) ? psProfile.customer_list.length : null) ??
        null;

      const vCustRaw =
        data?.u_customer_count ??
        data?.customer_count ??
        data?.customers_count ??
        data?.u_customers_count ??
        data?.profile?.u_customer_count ??
        data?.user?.u_customer_count ??
        // fallbacks if the API returns arrays instead of a numeric count
        (Array.isArray(data?.customers) ? data.customers.length : null) ??
        (Array.isArray(data?.customerList) ? data.customerList.length : null) ??
        (Array.isArray(data?.customer_list) ? data.customer_list.length : null) ??
        (Array.isArray(data?.profile?.customers) ? data.profile.customers.length : null) ??
        (Array.isArray(data?.user?.customers) ? data.user.customers.length : null) ??
        // fallback: what refreshProfile() may have put into the store
        psCustRaw ??
        null;


      const vCust = (vCustRaw === null || vCustRaw === undefined) ? null : Number(vCustRaw);


      if (!isMounted) return;

      setViewerCustomerFocus(focus ? String(focus).trim() : null);
      setViewerDisplayName(fullName || 'Viewer');
      setViewerProfessionText(viewerProfession ? String(viewerProfession).trim() : null);

      setViewerReviewCount(Number.isFinite(vCount) ? vCount : null);
      setViewerAvgRating(Number.isFinite(vAvg) ? vAvg : null);

      setViewerCustomerCount(Number.isFinite(vCust) ? vCust : null);


    } catch (err) {
      console.error('[CandidateListModal] Viewer profileStore refresh error:', err);
    } finally {
      if (isMounted) setIsLoadingViewerFocus(false);
    }
  };

  fetchViewerFromProfileStore();

  return () => {
    isMounted = false;
  };
}, [analysisOpen, myUid]);

// Panel 6 — Candidate REAL customer count (exclude "Imported" bucket like customerinfo.jsx)
useEffect(() => {
  if (!analysisOpen) return;
  if (!Number.isFinite(Number(candidateId))) return;

  let isMounted = true;

  const fetchCandidateCustomers = async () => {
    try {
      const res = await fetch(
        `/api/profile/customers?u_id=${candidateId}`,
        { method: 'GET', credentials: 'include' }
      );

      if (!res.ok) {
        console.error('[CandidateListModal] Failed to fetch candidate customers:', res.status);
        return;
      }

      const rows = await res.json();

      // Exclude the single "Imported Reviews" bucket row
      const realCustomers = Array.isArray(rows)
        ? rows.filter((c) => {
            const fn = String(c?.customer_firstname ?? '').trim().toLowerCase();
            const ln = String(c?.customer_lastname ?? '').trim().toLowerCase();

            // bucket row: Imported + Reviews
            if (fn === 'imported' && ln === 'reviews') return false;

            return true;
          })
        : [];



      if (!isMounted) return;
      setCandidateCustomerCount(realCustomers.length);
    } catch (err) {
      console.error('[CandidateListModal] Candidate customers fetch error:', err);
    }
  };

  fetchCandidateCustomers();

  return () => {
    isMounted = false;
  };
}, [analysisOpen, candidateId]);


  // Fetch profession exclusivity only when Analysis is opened
  useEffect(() => {
    if (!analysisOpen) return;
    if (!Number.isFinite(myUid) || myUid <= 0) return;
    if (!Number.isFinite(candidateId) || candidateId <= 0) return;

    let isMounted = true;

    const fetchExclusivity = async () => {
      try {
        setIsLoadingExclusivity(true);

        const res = await fetch(
          `/api/analysis/profession-exclusivity?viewerUId=${myUid}&candidateId=${candidateId}`,
          { method: 'GET', credentials: 'include' }
        );

        if (!res.ok) {
          console.error('[CandidateListModal] Failed to fetch exclusivity:', res.status);
          return;
        }

        const data = await res.json();

        if (isMounted) {
          setExclusivity(data && data.ok ? data : null);
        }
      } catch (err) {
        console.error('[CandidateListModal] Exclusivity fetch error:', err);
      } finally {
        if (isMounted) setIsLoadingExclusivity(false);
      }
    };

    fetchExclusivity();

    return () => {
      isMounted = false;
    };
  }, [analysisOpen, myUid, candidateId]);

  // Fetch service overlap only when Analysis is opened (Panel 2)
  useEffect(() => {
    if (!analysisOpen) return;
    if (!Number.isFinite(myUid) || myUid <= 0) return;
    if (!Number.isFinite(candidateId) || candidateId <= 0) return;

    let isMounted = true;

    const fetchServiceOverlap = async () => {
      try {
        setIsLoadingServiceOverlap(true);

        const res = await fetch(
          `/api/analysis/service-overlap?viewerUId=${myUid}&candidateId=${candidateId}`,
          { method: 'GET', credentials: 'include' }
        );

        if (!res.ok) {
          console.error('[CandidateListModal] Failed to fetch service overlap:', res.status);
          return;
        }

        const data = await res.json();

        if (isMounted) {
          setServiceOverlap(data && data.ok ? data : null);
        }
      } catch (err) {
        console.error('[CandidateListModal] Service overlap fetch error:', err);
      } finally {
        if (isMounted) setIsLoadingServiceOverlap(false);
      }
    };

    fetchServiceOverlap();

    return () => {
      isMounted = false;
    };
  }, [analysisOpen, myUid, candidateId]);

  // ── Fetch candidate's social platforms (ever connected) ──
  const [candidateSocialPlatforms, setCandidateSocialPlatforms] = useState(null);
  useEffect(() => {
    if (!analysisOpen || !Number.isFinite(candidateId) || candidateId <= 0) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(
          `/api/social/platforms/${candidateId}`,
          { credentials: 'include' }
        );
        const data = await resp.json();
        if (!cancelled && data.ok) setCandidateSocialPlatforms(data.platforms || []);
      } catch (err) {
        console.error('[social/platforms] fetch error:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [analysisOpen, candidateId]);

  // Fetch Matrix presence for this candidate via server-side admin API
  useEffect(() => {
    if (!opened || !Number.isFinite(candidateId) || candidateId <= 0) return;
    let cancelled = false;

    const fetchPresence = async () => {
      try {
        const res = await fetch(`/api/matrix/presence?uids=${candidateId}`, { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled || !data?.ok) return;
        const store = useMatrixStore.getState();
        for (const [uid, presence] of Object.entries(data.presence)) {
          store.setUserPresence(`@u${uid}:ehandshake.com`, presence);
        }
      } catch (err) {
        console.warn('[CandidateListModal] presence fetch error:', err);
      }
    };

    fetchPresence();
    const interval = setInterval(fetchPresence, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [opened, candidateId]);

  const onReviewsScroll = (e) => {
    const el = e.currentTarget;
    if (!el) return;

    // near-bottom trigger
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;

    if (!nearBottom) return;
    if (isFetchingMoreRef.current) return;
    if (visibleCount >= reviews.length) return;

    isFetchingMoreRef.current = true;

    setVisibleCount((prev) => {
      const next = prev + 8;
      return next > reviews.length ? reviews.length : next;
    });

    // small throttle so it doesn't fire repeatedly while pinned at bottom
    window.setTimeout(() => {
      isFetchingMoreRef.current = false;
    }, 150);
  };

  // Lock CandidateListModal to its current size, but allow dragging.
  // Initialize it from the existing "top/right/offsetRight" anchors so it opens in the same place.
  const MODAL_W = 420;
  const MODAL_H = 550;

  const [windowPos, setWindowPos] = useState(() => ({ x: 0, y: 0 }));

  useEffect(() => {
    if (!opened) return;

    const MODAL_W = 420;   // if you changed to 550 height, leave width as-is
    // Align modal's right edge with the right edge of the 90% container
    const containerMargin = Math.round(window.innerWidth * 0.05); // 5% margin on each side
    const x = Math.max(0, window.innerWidth - containerMargin - MODAL_W);

    const y = Math.max(0, Math.round(top - 38));

    setWindowPos({ x, y });
  }, [opened]);




    // Drag payload helper
  const DRAG_MIME = 'application/ehandshake-candidate';
  const onDragStartCandidate = (e) => {
    try {
      const candidate_u_id = Number(candidate?.u_id ?? candidate?.id ?? NaN);
      if (!Number.isFinite(candidate_u_id)) return;
      e.dataTransfer.setData(DRAG_MIME, JSON.stringify({ candidate_u_id }));
      // Optional: custom drag image (avatar)
      // const img = new Image();
      // img.src = candidate?.u_profile_picture || '/images/default-avatar.png';
      // e.dataTransfer.setDragImage(img, 16, 16);
    } catch {}
  };

  // Derive display fields from the candidate (fallbacks included)
  const displayFirst = candidate?.u_first_name || '';
  const displayLast = candidate?.u_last_name || '';
  const displayName = [displayFirst, displayLast].filter(Boolean).join(' ') || candidateName;
  const msgNameRaw = `${displayFirst} ${displayLast}`.trim();
  const messageLabel = `Message ${msgNameRaw || 'User'}`.trim();
  const messageLabelTrunc = messageLabel.length > 28 ? messageLabel.slice(0, 28) + '…' : messageLabel;

  // Common fields we've used elsewhere in your app
  const profileImg =
    candidate?.u_profile_picture ||
    candidate?.profileImg ||
    '/images/default-avatar.png'; // fallback (adjust to your actual default path)

const titleText =
  candidate?.u_job_title ??
  candidate?.u_title ??
  candidate?.u_role ??
  candidate?.title ??
  'Title';

const businessName = candidate?.b_name || 'Business Name';
const industryText = candidate?.b_industry || 'Industry';
// URL helpers for public pages + external site
const toCompanySlug = (raw) => {
  if (!raw) return '';
  const s = String(raw).trim();
  try {
    if (/^https?:\/\//i.test(s)) return new URL(s).hostname.replace(/\/+$/, '');
  } catch {}
  return s.replace(/^\/+|\/+$/g, '');
};

const userSlug = candidate?.u_url_name || candidate?.u_url_path || '';
const companySlug = toCompanySlug(candidate?.b_website || candidate?.website || '');

const userHref    = userSlug ? `${PUBLIC_SITE}/userprofiles/users/${userSlug}` : null;
const companyHref = companySlug ? `${PUBLIC_SITE}/userprofiles/companies/${companySlug}` : null;

const websiteHref = (() => {
  const raw = candidate?.b_website || candidate?.website || '';
  if (!raw) return null;
  const s = String(raw).trim();
  return /^https?:\/\//i.test(s) ? s : `https://${s.replace(/^\/+|\/+$/g, '')}`;
})();


// Format phone as xxx-xxx-xxxx when we have a US 10-digit (or 1+10) number; otherwise show as-is
const phoneText = (() => {
  const primary = candidate?.u_phone_number;
  const secondary = candidate?.b_phone;

  const raw =
    primary && String(primary).trim()
      ? primary
      : secondary && String(secondary).trim()
      ? secondary
      : '';

  const digits = String(raw).replace(/\D/g, '');
  if (digits.length === 10) return `${digits.slice(0,3)}-${digits.slice(3,6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits.startsWith('1')) return `${digits.slice(1,4)}-${digits.slice(4,7)}-${digits.slice(7)}`;
  return raw || 'Phone';
})();


// Normalize email to lowercase (display); if missing, keep placeholder
const emailText = candidate?.u_email ? String(candidate.u_email).toLowerCase() : 'Email';
// Use route values; provide safe fallbacks for "no reviews"
const safeCount = Number(candidate?.u_review_count ?? 0);
const rawAvg = candidate?.u_avg_rating;
const safeAvg = (rawAvg === null || rawAvg === undefined) ? 0 : Number(rawAvg);



console.log('[CandidateListModal] candidate:', candidate);
console.log('[CandidateListModal] b_name:', candidate?.b_name);
console.log('[CandidateListModal] b_industry:', candidate?.b_industry);
console.log('[CandidateListModal] b_phone:', candidate?.b_phone);
console.log('[CandidateListModal] u_email:', candidate?.u_email);
console.log('[CandidateListModal] derived ->', { industryText, phoneText, emailText });





  useEffect(() => {
    if (opened) {
      bringToFront('candidate'); // ensure top when opened OR when candidate changes
      const id = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(id);
    }
    setVisible(false);
  }, [opened, candidateId, bringToFront]);



  if (!opened) return null;

  // Panel 5: Scores button color should reflect the LOWEST score between viewer & candidate
  const getScoreBand = (val) => {
    if (val === null || val === undefined) return 'GRAY';
    const n = Number(val);
    if (!Number.isFinite(n) || n <= 0) return 'RED';     // treat 0 / invalid as "under 3.0"
    if (n >= 4.0) return 'GREEN';
    if (n >= 3.0) return 'YELLOW';
    return 'RED';
  };

  const getBandBtnClass = (band) => {
    if (band === 'GREEN') return 'bg-green-50 border-green-300';
    if (band === 'YELLOW') return 'bg-yellow-50 border-yellow-300';
    if (band === 'RED') return 'bg-red-50 border-red-300';
    return 'bg-gray-50 border-gray-200';
  };

  const getStatusFill = (band) => {
    if (band === 'GREEN') return '#16a34a';
    if (band === 'YELLOW') return '#fde047';
    if (band === 'RED') return '#dc2626';
    return '#9ca3af';
  };

  return (

    <>
      {/* Fixed-position panel aligned to Column 3 via top/right pixels */}
      <Rnd
        position={windowPos}
        enableResizing={false}
        dragHandleClassName="eh-candidate-header"
        bounds="window"
        dragAxis="both"
        onDrag={(e, d) => {
          setWindowPos({ x: d.x, y: d.y });
        }}
        onDragStop={(e, d) => {
          setWindowPos({ x: d.x, y: d.y });
        }}
        style={{ zIndex: candidateZ, position: 'fixed' }}
        onMouseDownCapture={() => bringToFront('candidate')}
      >

        <div
          className={[
            "pointer-events-auto",
            "w-[420px] h-[550px]",                 // locked size (must not change)
            "enableResizing={false}",
            "bg-white rounded-xl shadow-[0_10px_30px_rgba(0,0,0,0.12)]",
            "flex flex-col overflow-hidden",
            "transition-opacity duration-1000",
            visible ? "opacity-100" : "opacity-0",
          ].join(" ")}
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-center justify-between px-3 py-0 bg-[#00aef7] eh-candidate-header cursor-move">
            <h3
              className="text-base font-semibold text-white truncate cursor-grab active:cursor-grabbing"
              draggable
              onDragStart={onDragStartCandidate}
              title="Drag this candidate to the Master Recruiting List"
            >
              {displayName}
            </h3>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClose?.();
                }}
                className="eh-candidate-nodrag rounded-full p-2 hover:bg-white/10 focus:outline-none text-white"
                aria-label="Close"
                title="Close"
              >

              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* CONTENT */}
          <div className="flex-1 overflow-hidden px-4 py-1 flex flex-col">
            {/* Row 1A: Business header (full width, centered, brand blue) */}
            <div className="mb-0">
              <div className="text-lg font-semibold text-[#00aef7] leading-tight text-center">
                {businessName}
              </div>
            </div>

            {/* Row 1B: two columns */}
            <div className="grid grid-cols-[128px_1fr] gap-4">
              {/* Col 1: Profile picture only */}
              <div className="flex flex-col items-center text-center">
                <img
                  src={profileImg}
                  alt={displayName}
                  className={`w-28 h-28 rounded-full object-cover cursor-grab active:cursor-grabbing ${
                    candidatePresence === 'online'
                      ? 'border-2 border-green-500'
                      : candidatePresence === 'unavailable'
                        ? 'border-2 border-yellow-500'
                        : 'border border-[#00aef7]/20'
                  }`}
                  onError={(e) => { e.currentTarget.src = '/images/default-avatar.png'; }}
                  draggable
                  onDragStart={onDragStartCandidate}
                  title="Drag this candidate to the Master Recruiting List"
                />
              </div>

              {/* Col 2: Industry (larger), Name, Title, Phone, Email (top-aligned) */}
              <div className="flex flex-col justify-start gap-0">
                <div className="text-base font-semibold text-gray-900">{industryText}</div>
                <div className="text-xs text-gray-600">{titleText}</div>
                <div className="text-sm text-gray-700">{phoneText}</div>
                <div className="text-sm text-gray-700 break-all">{emailText}</div>

                {/* Stars + "X.X (N reviews)" like CompanyProfile (show only when count > 0) */}
                <div className="mt-1 flex items-center gap-2">
                  <span className={safeCount > 0 ? "" : "opacity-50"}>
                    <StarRating rating={safeAvg} />
                  </span>
                  <span className="text-xs text-gray-600">
                    {safeAvg.toFixed(1)} ({safeCount} reviews)
                  </span>
                </div>
              </div>
            </div>

            {/* Row 2: actions – single icon row (6 columns), no visible text labels */}
            <div className="my-1 py-1 border-y border-gray-200">
              <div className="-my-1 -py-1 grid grid-cols-6 justify-items-center items-center w-full gap-2">
                {/* 1) Message */}
                <button
                  type="button"
                  disabled={isSelf}
                  onClick={async () => {
                    if (isSelf) return;

                    if (!Number.isFinite(myUid) || !Number.isFinite(candidateId)) {
                      console.error('[CandidateListModal] Invalid user IDs for messaging:', { myUid, candidateId });
                      return;
                    }

                    const a = Math.min(myUid, candidateId);
                    const b = Math.max(myUid, candidateId);
                    const aliasFull = `#eh.dm.u${a}_u${b}:ehandshake.com`;

                    // Load fresh conversations from server to check for existing DM
                    try {
                      await useConversationStore.getState().loadConversations();
                    } catch (err) {
                      console.warn('[CandidateListModal] Failed to load conversations:', err);
                    }

                    // Check for existing DM in conversationStore (active Matrix DM or accepted dm_relationship)
                    const convState = useConversationStore.getState();
                    const existingActiveDM = (convState.conversations || []).find((c) => {
                      if (c.conversation_type !== 'active_matrix_dm' && c.conversation_type !== 'accepted_dm') return false;
                      return Number(c.other_user_id) === Number(candidateId);
                    });

                    if (existingActiveDM) {
                      // Already have an active chat - just focus on it
                      console.log('[CandidateListModal] Found existing active DM, focusing:', existingActiveDM);

                      // Use the room_id if available, otherwise fall back to alias
                      const threadRef = existingActiveDM.room_id || aliasFull;
                      setSelectedThread(threadRef);

                      if (existingActiveDM.room_id) {
                        useMatrixStore.getState().setSelectedRoomId(existingActiveDM.room_id);
                      }

                      openChatCenter();
                      return;
                    }

                    // Check for existing optimistic/pending thread
                    const msgState = useMessagingStore.getState();
                    const existingThread =
                      (msgState.optimisticThreads || []).find((t) => {
                        const sameAlias = String(t.alias || '') === String(aliasFull);
                        const samePeer = Number(t.peerUid) === Number(candidateId);
                        return sameAlias || samePeer;
                      }) || null;

                    if (!existingThread) {
                      createLocalInviteThread({
                        candidateId,
                        candidateName: displayName,
                        candidateAvatar: profileImg,
                        candidateBusiness: businessName,
                      });
                    }

                    setSelectedThread(aliasFull);

                    openWithCandidate({
                      candidateId,
                      candidateName: displayName,
                      candidateAvatar: profileImg,
                      candidateBusiness: businessName
                    });

                    openChatCenter();
                  }}
                  className={[
                    "h-10 w-10 inline-flex items-center justify-center rounded-md transition",
                    "bg-transparent",
                    "focus:outline-none focus:ring-2 focus:ring-[#00aef7]/30",
                    isSelf
                      ? "text-gray-300 cursor-not-allowed"
                      : candidatePresence === 'online'
                        ? "text-green-500 hover:bg-green-500/10"
                        : "text-[#00aef7] hover:bg-[#00aef7]/10"
                  ].join(" ")}

                  title={isSelf ? "You cannot message yourself." : "Message"}
                  aria-label={isSelf ? "Message (disabled)" : "Message"}
                >
                    <svg viewBox="0 0 576 512" height="20" width="20" xmlns="http://www.w3.org/2000/svg" fill={isSelf ? 'currentColor' : candidatePresence === 'online' ? '#22c55e' : '#00aef7'}>
                      <path d="M416 192c0-88.4-93.1-160-208-160S0 103.6 0 192c0 34.3 14.1 65.9 38 92-13.4 30.2-35.5 54.2-35.8 54.5-2.2 2.3-2.8 5.7-1.5 8.7S4.8 352 8 352c36.6 0 66.9-12.3 88.7-25 32.2 15.7 70.3 25 111.3 25 114.9 0 208-71.6 208-160zm122 220c23.9-26 38-57.7 38-92 0-66.9-53.5-124.2-129.3-148.1.9 6.6 1.3 13.3 1.3 20.1 0 105.9-107.7 192-240 192-10.8 0-21.3-.8-31.7-1.9C207.8 439.6 281.8 480 368 480c41 0 79.1-9.2 111.3-25 21.8 12.7 52.1 25 88.7 25 3.2 0 6.1-1.9 7.3-4.8 1.3-2.9.7-6.3-1.5-8.7-.3-.3-22.4-24.2-35.8-54.5z"></path>
                    </svg>
                </button>

                {/* 2) Add to Recruiting List (same logic, icon-only) */}
                <AddToRecruitButton candidate={candidate} viewerUId={viewerUId} inline variant="icon" />

                {/* 3) Analyze */}
                <button
                  type="button"
                  onClick={() => setAnalysisOpen((v) => !v)}
                  className={[
                    "h-10 w-10 inline-flex items-center justify-center rounded-md",
                    "bg-transparent border-0 appearance-none",
                    "transition",
                    "outline-none focus:outline-none",
                    analysisOpen ? "bg-[#00aef7]/10" : "",
                    "text-[#00aef7] hover:bg-[#00aef7]/10"
                  ].join(" ")}
                  title={analysisOpen ? "Hide Analysis" : "Analyze"}
                  aria-label={analysisOpen ? "Hide Analysis" : "Analyze"}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#00aef7"><path d="M400-40v-80H200q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h200v-80h80v880h-80ZM200-240h200v-240L200-240Zm360 120v-360l200 240v-520H560v-80h200q33 0 56.5 23.5T840-760v560q0 33-23.5 56.5T760-120H560Z"/></svg>

                </button>

                {/* 4) View my Public Profile */}
                {userHref ? (
                  <a
                    href={userHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={[
                      "h-10 w-10 inline-flex items-center justify-center rounded-md",
                      "bg-transparent border-0 outline-none",
                      "transition",
                      "text-[#00aef7] hover:bg-[#00aef7]/10"
                    ].join(" ")}
                    title="View my Public Profile"
                    aria-label="View my Public Profile (opens in a new tab)"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#00aef7"><path d="M840-120v-640H120v320H40v-320q0-33 23.5-56.5T120-840h720q33 0 56.5 23.5T920-760v560q0 33-23.5 56.5T840-120ZM247-447q-47-47-47-113t47-113q47-47 113-47t113 47q47 47 47 113t-47 113q-47 47-113 47t-113-47Zm169.5-56.5Q440-527 440-560t-23.5-56.5Q393-640 360-640t-56.5 23.5Q280-593 280-560t23.5 56.5Q327-480 360-480t56.5-23.5ZM40-80v-112q0-34 17.5-62.5T104-298q62-31 126-46.5T360-360q66 0 130 15.5T616-298q29 15 46.5 43.5T680-192v112H40Zm80-80h480v-32q0-11-5.5-20T580-226q-54-27-109-40.5T360-280q-56 0-111 13.5T140-226q-9 5-14.5 14t-5.5 20v32Zm240-400Zm0 400Z"/></svg>

                  </a>

                ) : (
                  <span
                    className={[
                      "h-10 w-10 inline-flex items-center justify-center rounded-md",
                      "bg-transparent border-0 appearance-none",
                      "text-gray-300 cursor-not-allowed"
                    ].join(" ")}
                    title="My Public Profile unavailable"
                    aria-label="My Public Profile unavailable"
                  >

                  </span>
                )}


                {/* 5) View my Company Profile */}
                {companyHref ? (
                  <a
                    href={companyHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={[
                      "h-10 w-10 inline-flex items-center justify-center rounded-md",
                      "bg-transparent border-0 outline-none",
                      "transition",
                      "text-[#00aef7] hover:bg-[#00aef7]/10"
                    ].join(" ")}
                    title="View my Public Company Profile"
                    aria-label="View my Company Profile (opens in a new tab)"
                  >
                    <span className="inline-flex scale-[0.7] origin-center">
                    <svg xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 -960 960 960"
                        height="24px"
                        width="24px"
                        style={{
                          color: "#00aef7",
                          transform: "scale(1.55) translateY(4px)",
                          transformOrigin: "center"
                        }}
                        fill="currentColor"
                        aria-hidden="true">
                      <g transform="translate(-40,-120)">
                        <path d="m 142.59218,-121.36089 v -583.2 h 324 v 129.6 h 324 v 453.6 z m 64.8,-64.8 h 194.4 v -64.8 h -194.4 z m 0,-129.6 h 194.4 v -64.8 h -194.4 z m 0,-129.6 h 194.4 v -64.8 h -194.4 z m 0,-129.6 h 194.4 v -64.8 h -194.4 z m 259.2,388.8 h 259.2 v -324 h -259.2 z m 64.8,-194.4 v -64.8 h 129.6 v 64.8 z m 0,129.6 v -64.8 h 129.6 v 64.8 z" />
                        <path d="m 120,-840 c -21.999934,0 -40.817756,7.84901 -56.484375,23.51562 C 47.849006,-800.81774 40,-781.99994 40,-760 v 320 h 80 v -320 h 720 v 640 c 21.99994,0 40.81774,-7.84901 56.48438,-23.51562 C 912.15099,-159.18226 920,-178.00006 920,-200 v -560 c 0,-21.99994 -7.84901,-40.81774 -23.51562,-56.48438 C 880.81774,-832.15099 861.99994,-840 840,-840 Z m 560,728.67188 c -0.25096,0.32612 -0.44855,0.69251 -0.70312,1.01562 0.21035,0.17964 0.4866,0.29512 0.70312,0.46875 z M 53.90625,-88.203125 c -0.416854,0.115393 -0.841286,0.175306 -1.25,0.3125 -0.146623,0.04922 0.319779,0.04157 0.46875,0 0.401407,-0.112019 0.480336,-0.22025 0.78125,-0.3125 z" />
                      </g>
                    </svg>

                    </span>
                  </a>

                ) : (
                  <span
                    className={[
                      "h-10 w-10 inline-flex items-center justify-center rounded-md",
                      "bg-transparent border-0",
                      "text-gray-300 cursor-not-allowed"
                    ].join(" ")}
                    title="Company Profile unavailable"
                    aria-label="Company Profile unavailable"
                  >
                    <svg
                      viewBox="0 0 480.40309 496"
                      width="30"
                      height="30"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                    >
                      {/* OPTIONAL: paste same nodes here for a disabled icon */}
                    </svg>
                  </span>
                )}


                {/* 6) View Company Website */}
                {websiteHref ? (
                  <a
                    href={websiteHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={[
                      "h-10 w-10 inline-flex items-center justify-center rounded-md",
                      "bg-transparent border-0 outline-none",
                      "transition",
                      "text-[#00aef7] hover:bg-[#00aef7]/10"
                    ].join(" ")}
                    title="View My Company Website"
                    aria-label="View Company Website (opens in a new tab)"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="#00aef7"
                      width="25"
                      height="25"
                    >
                      <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zM15 18H4v-4h11v4zm0-5H4V9h11v4zm5 5h-4V9h4v9z" />
                    </svg>
                  </a>
                ) : (
                  <span
                    className={[
                      "h-10 w-10 inline-flex items-center justify-center rounded-md",
                      "bg-transparent border-0",
                      "text-gray-300 cursor-not-allowed"
                    ].join(" ")}
                    title="Company Website unavailable"
                    aria-label="Company Website unavailable"
                  >
                    <svg
                      viewBox="0 0 496 512"
                      width="30"
                      height="30"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                    >
                      {/* OPTIONAL: paste same nodes here for disabled icon */}
                    </svg>
                  </span>
                )}

              </div>
            </div>

          {analysisOpen && (
            <div
              className="mt-0 rounded-lg border border-gray-200 bg-white px-1 py-1"
              onMouseLeave={() => setAnalysisHoverToast(null)}
            >


              {/* Hover Toast (floating overlay; bottom fixed; expands upward) */}
              {(() => {
                const t = analysisHoverToast;

                const ui =
                  !t
                    ? { wrap: 'border-blue-300 bg-[#00aef7]', text: 'text-[#ffffff]' }
                    : t.status === 'GREEN'
                      ? { wrap: 'border-green-300 bg-white', text: 'text-gray-500' }
                      : t.status === 'RED'
                        ? { wrap: 'border-red-300 bg-white', text: 'text-gray-500' }
                        : t.status === 'YELLOW'
                          ? { wrap: 'border-yellow-300 bg-white', text: 'text-gray-500' }
                          : { wrap: 'border-gray-300 bg-white', text: 'text-gray-700' };



                const msg = t?.message || 'Hover over a panel to see the details. This is a user to user comparison. Not a team comparison. Drag the user to your team recruiting list to compare.';

                // Title mapping based on kind
                const titleMap = {
                  'CUSTOMER_FOCUS': 'Customer Focus',
                  'SERVICE_OVERLAP': 'Service Area',
                  'PROFESSION_EXCLUSIVITY': 'Profession Exclusivity',
                  'REVIEW_COUNT': 'Review Quantity',
                  'SCORES': 'Score Summary',
                  'CUSTOMERS': 'Customer Counts',
                  'SOCIAL': 'Social Channels',
                  'PROFILE_CHECK': 'Profile Check',
                };
                const title = t?.kind ? titleMap[t.kind] : null;

                return (
                  <>
                    {/* Spacer keeps the toast bottom anchored at the same visual position */}
                    <div className="h-7 mb-0" />

                    {/* Floating toast: bottom sits at the spacer's bottom; grows upward */}
                    <div className="relative">
                      <div className={`absolute bottom-[5px] left-0 right-0 z-50 rounded-md border px-2 py-0.5 shadow-sm ${ui.wrap}`}>
                        <div className="flex items-start gap-3">
                          {/* Left column: text */}
                          <div className={`text-[10px] font-semibold ${ui.text} flex-1`}>
                            {title && <div className="text-sm font-bold text-[#00aef7] mb-1">{title}</div>}
                            {msg}
                          </div>

                          {/* Right column: mini map (layout only for now) */}
                          {t?.kind === 'SERVICE_OVERLAP' && (
                            <div className="shrink-0 w-[170px]">
                              <div className="h-[110px] w-full rounded-md border border-gray-200 bg-white overflow-hidden">
                                {(() => {
                                  const mapUrl = buildServiceOverlapMapUrl(serviceOverlap);

                                  console.log('SERVICE OVERLAP OBJECT:', serviceOverlap);
                                  console.log('SERVICE OVERLAP MAP URL GENERATED:', mapUrl);

                                  if (!mapUrl) {
                                    return (
                                      <div className="h-full w-full flex items-center justify-center">
                                        <div className="text-[10px] text-gray-400 font-semibold">
                                          Map unavailable
                                        </div>
                                      </div>
                                    );
                                  }

                                  return (
                                    <img
                                      src={mapUrl}
                                      alt="Service area"
                                      className="w-full h-full object-cover"
                                    />
                                  );
                                })()}

                              </div>
                            </div>
                          )}

                        </div>
                      </div>
                    </div>

                  </>
                );
              })()}


              {/* 8-panel grid (circles on 1 row, hover triggers toast) */}
              <div className="flex justify-center gap-2">

              {/* 1) Customer Focus */}
              {(() => {
                const viewerFull = (viewerDisplayName || 'Viewer').trim();
                const candidateFull = (displayName || 'Candidate').trim();

                const viewerFirst = viewerFull.split(' ')[0];
                const candidateFirst = candidateFull.split(' ')[0];

                const viewerFocusRaw = viewerCustomerFocus ? String(viewerCustomerFocus).trim() : '';
                const candidateFocusRaw = candidate?.u_customer_focus ? String(candidate.u_customer_focus).trim() : '';

                const focusInfo = (v) => {
                  const s = (v || '').toString().trim();
                  const k = s.toLowerCase();

                  if (k === 'b2b') return { audience: 'businesses', code: 'B2B' };
                  if (k === 'b2c') return { audience: 'consumers', code: 'B2C' };
                  if (k === 'b2bc') return { audience: 'businesses & consumers', code: 'B2BC' };

                  return { audience: 'customers', code: (s || 'Unknown') };
                };

                const viewer = focusInfo(viewerFocusRaw);
                const candidateFocus = focusInfo(candidateFocusRaw);

                const isMissing = !viewerFocusRaw || !candidateFocusRaw;
                const isMatch = !isMissing && viewerFocusRaw.toLowerCase() === candidateFocusRaw.toLowerCase();

                const status = isLoadingViewerFocus
                  ? 'GRAY'
                  : (isMatch ? 'GREEN' : 'YELLOW');

                const message = isLoadingViewerFocus ? (
                  'Customer focus is loading…'
                ) : isMissing ? (
                  'Customer focus is inconclusive (missing data for one or both users).'
                ) : (
                  <div className="space-y-2">
                    <div>
                      <div>{viewerFirst} calls on {viewer.audience}: {viewer.code}</div>
                      <div>{candidateFirst} calls on {candidateFocus.audience}: {candidateFocus.code}</div>
                    </div>

                    {isMatch ? (
                      <div>
                        <div>This is a good match. You have the same customer focus.</div>
                        <div>You serve the same type of customers.</div>
                      </div>
                    ) : (
                      <div>
                        <div>This is not a great match. You do not share the same customer focus or you partially share the focus. To increase your market exposure, team with users that only share your same customer focus.</div>
                      </div>
                    )}
                  </div>
                );

                const cls =
                  status === 'GREEN'
                    ? 'bg-green-50 border-green-300'
                    : status === 'YELLOW'
                      ? 'bg-yellow-50 border-yellow-300'
                      : 'bg-gray-50 border-gray-200';

                return (
                  <button
                    type="button"
                    onClick={(e) => e.preventDefault()}
                    onMouseEnter={() => setAnalysisHoverToast({ status, message, kind: 'CUSTOMER_FOCUS' })}
                    onMouseLeave={() => setAnalysisHoverToast(null)}
                    className="w-8 h-8 flex items-center justify-center cursor-pointer transition focus:outline-none"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" height="22px" viewBox="0 -960 960 960" width="22px" fill={getStatusFill(status)}><path d="M200-120q-33 0-56.5-23.5T120-200v-160h80v160h160v80H200Zm400 0v-80h160v-160h80v160q0 33-23.5 56.5T760-120H600ZM120-600v-160q0-33 23.5-56.5T200-840h160v80H200v160h-80Zm640 0v-160H600v-80h160q33 0 56.5 23.5T840-760v160h-80ZM338.5-338.5Q280-397 280-480t58.5-141.5Q397-680 480-680t141.5 58.5Q680-563 680-480t-58.5 141.5Q563-280 480-280t-141.5-58.5ZM565-395q35-35 35-85t-35-85q-35-35-85-35t-85 35q-35 35-35 85t35 85q35 35 85 35t85-35Zm-85-85Z"/></svg>
                  </button>
                );
              })()}




{/* 2) Service Distance */}
                <button
                  type="button"
                  onClick={(e) => e.preventDefault()}
                  onMouseEnter={() => {
                    if (isLoadingServiceOverlap) {
                      setAnalysisHoverToast({ status: 'GRAY', message: 'Service Area is loading…', kind: 'SERVICE_OVERLAP' });
                      return;
                    }

                    const viewerName = (viewerDisplayName || 'Viewer').trim();
                    const candidateNameText = (displayName || 'Candidate').trim();

                    const viewerFirst = (viewerName.split(' ')[0] || viewerName).trim();
                    const candidateFirst = (candidateNameText.split(' ')[0] || candidateNameText).trim();

 const overlapPct = Number(serviceOverlap?.overlap_percent);
                    const viewerRadius = Number(serviceOverlap?.viewer_radius_miles);
                    const candidateRadius = Number(serviceOverlap?.candidate_radius_miles);

                    const hasOverlapPct = Number.isFinite(overlapPct);
                    const hasViewerRadius = Number.isFinite(viewerRadius);
                    const hasCandidateRadius = Number.isFinite(candidateRadius);

                    // Status still follows backend, but we also fall back cleanly.
                    const status = serviceOverlap?.status === 'GREEN' ? 'GREEN' : 'YELLOW';

                    // overlapPct is % of the smaller service area (backend definition).
                    // We also compute the corresponding % for the larger service area using area ratio.
                    let viewerOverlapPct = overlapPct;
                    let candidateOverlapPct = overlapPct;

                    const hasValidData =
                      Number.isFinite(overlapPct) &&
                      Number.isFinite(viewerRadius) &&
                      Number.isFinite(candidateRadius);

                    if (hasValidData) {
                      const rSmall = Math.min(viewerRadius, candidateRadius);
                      const rLarge = Math.max(viewerRadius, candidateRadius);

                      const pctLarge = (overlapPct * (rSmall * rSmall) / (rLarge * rLarge));

                      if (viewerRadius <= candidateRadius) {
                        viewerOverlapPct = overlapPct;
                        candidateOverlapPct = pctLarge;
                      } else {
                        viewerOverlapPct = pctLarge;
                        candidateOverlapPct = overlapPct;
                      }

                      viewerOverlapPct = Math.max(0, Math.min(100, viewerOverlapPct));
                      candidateOverlapPct = Math.max(0, Math.min(100, candidateOverlapPct));
                    }

                    let overlapTier = 'UNKNOWN';

                    if (hasValidData) {
                      if (overlapPct < 25) overlapTier = 'WEAK';
                      else if (overlapPct < 50) overlapTier = 'ACCEPTABLE';
                      else if (overlapPct < 75) overlapTier = 'GOOD';
                      else overlapTier = 'STRONG';
                    }

                    const viewerCoversCandidate = hasValidData && viewerRadius >= candidateRadius;
                    const candidateCoversViewer = hasValidData && candidateRadius > viewerRadius;

                    // Short, readable assessment (no distance/radius paragraph)
                    let assessmentText = '';

                    if (!hasValidData) {
                      assessmentText = 'Service area information is incomplete.';
                    } else if (overlapTier === 'WEAK') {
                      assessmentText = 'Weak overlap. Very little shared territory.';
                    } else if (overlapTier === 'ACCEPTABLE') {
                      assessmentText = 'Acceptable overlap. Some shared territory, but limited coverage.';
                    } else if (overlapTier === 'GOOD') {
                      assessmentText = 'Good overlap. A large portion of your service areas overlap.';
                    } else if (overlapTier === 'STRONG') {
                      if (viewerCoversCandidate) {
                        assessmentText = `Strong overlap with you. All of ${candidateFirst}'s service area is within your coverage, but your service area extends beyond theirs.`;
                      } else if (candidateCoversViewer) {
                        assessmentText = `Strong overlap with ${candidateFirst}. All of your service area is within ${candidateFirst}'s coverage, but their service area extends beyond yours.`;
                      } else {
                        assessmentText = 'Strong overlap. Your service areas overlap heavily.';
                      }
                    }

                    const message = !hasOverlapPct
                      ? (serviceOverlap?.message || 'Service Area is inconclusive (missing service radius or location).')
                      : (
                        <div className="space-y-1">
                          <div>
                            {`The overlap is ${viewerOverlapPct.toFixed(1)}% for you ${viewerFirst}. It is ${candidateOverlapPct.toFixed(1)}% for ${candidateFirst}.`}
                          </div>
                          {assessmentText ? <div>{assessmentText}</div> : null}
                        </div>
                      );




                    setAnalysisHoverToast({ status, message, kind: 'SERVICE_OVERLAP' });
                  }}
                  onMouseLeave={() => setAnalysisHoverToast(null)}
                  className="w-8 h-8 flex items-center justify-center cursor-pointer transition focus:outline-none"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" height="22px" viewBox="0 -960 960 960" width="22px" fill={getStatusFill(isLoadingServiceOverlap ? 'GRAY' : serviceOverlap?.status === 'GREEN' ? 'GREEN' : 'YELLOW')}><path d="M162-162q-82-82-82-198t82-198q82-82 198-82t198 82q82 82 82 198t-82 198q-82 82-198 82t-198-82Zm339.5-56.5Q560-277 560-360t-58.5-141.5Q443-560 360-560t-141.5 58.5Q160-443 160-360t58.5 141.5Q277-160 360-160t141.5-58.5ZM678-332q2-6 2-13v-15q0-133-93.5-226.5T360-680h-15q-7 0-13 2 26-88 98.5-145T600-880q116 0 198 82t82 198q0 97-57 169.5T678-332Z"/></svg>
                </button>



                {/* 3) Profession Exclusivity */}
                {(() => {
                  const viewerName = (viewerDisplayName || 'Viewer').trim();
                  const candidateNameText = (displayName || 'Candidate').trim();

                  const viewerFirst = (viewerName.split(' ')[0] || viewerName).trim();
                  const candidateFirst = (candidateNameText.split(' ')[0] || candidateNameText).trim();

                  // DISPLAY TEXT ONLY:
                  // Always show the business's current industry text (b_business_industry / b_industry),
                  // not inferred canonical profession labels.
                  const viewerIndustryText =
                    (viewerProfessionText ? String(viewerProfessionText).trim() : '') ||
                    (exclusivity?.viewer?.b_business_industry ? String(exclusivity.viewer.b_business_industry).trim() : '') ||
                    (exclusivity?.viewer?.b_industry ? String(exclusivity.viewer.b_industry).trim() : '');

                  const candidateIndustryText =
                    (candidate?.b_business_industry ? String(candidate.b_business_industry).trim() : '') ||
                    (candidate?.b_industry ? String(candidate.b_industry).trim() : '') ||
                    (exclusivity?.candidate?.b_business_industry ? String(exclusivity.candidate.b_business_industry).trim() : '') ||
                    (exclusivity?.candidate?.b_industry ? String(exclusivity.candidate.b_industry).trim() : '');

                  const isMissing = !viewerIndustryText || !candidateIndustryText;

                  // If backend status is missing, fall back to a simple text compare (display-only).
                  const textSame =
                    !isMissing &&
                    String(viewerIndustryText).toLowerCase() === String(candidateIndustryText).toLowerCase();

                  // STATUS MUST OBEY BACKEND FIRST (fixes GREEN/RED mismatch)
                  const status = isLoadingExclusivity
                    ? 'GRAY'
                    : (exclusivity?.status
                        ? String(exclusivity.status).toUpperCase()
                        : (isMissing ? 'YELLOW' : (textSame ? 'RED' : 'GREEN'))
                      );

                  const message = isLoadingExclusivity ? (
                    'Profession Exclusivity is loading…'
                  ) : isMissing ? (
                    'Profession exclusivity is inconclusive (missing b_business_industry for one or both users).'
                  ) : (
                    <div className="space-y-2">
                      <div>
                        <div>{viewerFirst}'s profession is: {viewerIndustryText}</div>
                        <div>{candidateFirst}'s profession is: {candidateIndustryText}</div>
                      </div>


                      {status === 'RED' ? (
                        <div>
                          <div>They have the same profession. This violates the "One profession per team" rule. This is not a good match.</div>
                          
                        </div>
                      ) : (
                        <div>
                          <div>{viewerFirst} &amp; {candidateFirst} have different professions. There is no exclusivity conflict. You would go well on a new team together. Drag & drop {candidateFirst} onto any existing team to see the team profession exclusivity results.</div>
                        </div>
                      )}
                    </div>
                  );

                  const cls =
                    status === 'GREEN'
                      ? 'bg-green-50 border-green-300'
                      : status === 'RED'
                        ? 'bg-red-50 border-red-300'
                        : status === 'YELLOW'
                          ? 'bg-yellow-50 border-yellow-300'
                          : 'bg-gray-50 border-gray-200';

                  return (
                    <button
                      type="button"
                      onClick={(e) => e.preventDefault()}
                      onMouseEnter={() => setAnalysisHoverToast({ status, message, kind: 'PROFESSION_EXCLUSIVITY' })}
                      onMouseLeave={() => setAnalysisHoverToast(null)}
                      className="w-8 h-8 flex items-center justify-center cursor-pointer transition focus:outline-none"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" height="22px" viewBox="0 -960 960 960" width="22px" fill={getStatusFill(status)}><path d="M481-781q106 0 200 45.5T838-604q7 9 4.5 16t-8.5 12q-6 5-14 4.5t-14-8.5q-55-78-141.5-119.5T481-741q-97 0-182 41.5T158-580q-6 9-14 10t-14-4q-7-5-8.5-12.5T126-602q62-85 155.5-132T481-781Zm0 94q135 0 232 90t97 223q0 50-35.5 83.5T688-257q-51 0-87.5-33.5T564-374q0-33-24.5-55.5T481-452q-34 0-58.5 22.5T398-374q0 97 57.5 162T604-121q9 3 12 10t1 15q-2 7-8 12t-15 3q-104-26-170-103.5T358-374q0-50 36-84t87-34q51 0 87 34t36 84q0 33 25 55.5t59 22.5q34 0 58-22.5t24-55.5q0-116-85-195t-203-79q-118 0-203 79t-85 194q0 24 4.5 60t21.5 84q3 9-.5 16T208-205q-8 3-15.5-.5T182-217q-15-39-21.5-77.5T154-374q0-133 96.5-223T481-687Zm0-192q64 0 125 15.5T724-819q9 5 10.5 12t-1.5 14q-3 7-10 11t-17-1q-53-27-109.5-41.5T481-839q-58 0-114 13.5T260-783q-8 5-16 2.5T232-791q-4-8-2-14.5t10-11.5q56-30 117-46t124-16Zm0 289q93 0 160 62.5T708-374q0 9-5.5 14.5T688-354q-8 0-14-5.5t-6-14.5q0-75-55.5-125.5T481-550q-76 0-130.5 50.5T296-374q0 81 28 137.5T406-123q6 6 6 14t-6 14q-6 6-14 6t-14-6q-59-62-90.5-126.5T256-374q0-91 66-153.5T481-590Zm-1 196q9 0 14.5 6t5.5 14q0 75 54 123t126 48q6 0 17-1t23-3q9-2 15.5 2.5T744-191q2 8-3 14t-13 8q-18 5-31.5 5.5t-16.5.5q-89 0-154.5-60T460-374q0-8 5.5-14t14.5-6Z"/></svg>
                    </button>
                  );
                })()}




                {/* 4) Review Quantity */}
                <button
                  type="button"
                  onClick={(e) => e.preventDefault()}
                  onMouseEnter={() => {
                    const candidateNameText = (displayName || 'Candidate').trim();
                    const candidateFirst = (candidateNameText.split(' ')[0] || candidateNameText).trim();

                    const count = reviewCount;
                    console.log('[Review Quantity]', {
                      candidateId,
                      candidateName: displayName,
                      isLoadingReviews,
                      reviewsIsArray: Array.isArray(reviews),
                      reviewsLength: Array.isArray(reviews) ? reviews.length : null,
                      reviewCountUsed: count
                    });
                    let status = 'RED';
                    if (count >= 5) status = 'GREEN';
                    else if (count >= 1) status = 'YELLOW';

                    let message;

                    if (status === 'GREEN') {
                      message = (
                        <div className="space-y-1">
                          <div>
                            {candidateFirst} has {count} reviews. That's a good start.
                          </div>
                          <div>
                            But the more reviews the better. You may want to chat with {candidateFirst} and encourage them to import more Google reviews or use eHandshake's "invitation only" review system.
                          </div>
                          <div>
                            We live in a review-driven world, and reviews matter.
                          </div>
                        </div>
                      );
                    } else if (status === 'YELLOW') {
                      message = (
                        <div className="space-y-1">
                          <div>
                            {candidateFirst} has {count} review{count === 1 ? '' : 's'}.
                          </div>
                          <div>
                            That's a good start, but the more reviews the better. You may want to chat with {candidateFirst} and encourage them to import more Google reviews or use eHandshake's "invitation only" review system.
                          </div>
                          <div>
                            We live in a review-driven world, and reviews matter.
                          </div>
                        </div>
                      );
                    } else {
                      message = (
                        <div className="space-y-1">
                          <div>
                            {candidateFirst} has no reviews yet.
                          </div>
                          <div>
                            You may want to chat with {candidateFirst} and encourage them to import Google reviews or use eHandshake's "invitation only" review system.
                          </div>
                          <div>
                            The more reviews a business has, the better.
                          </div>
                        </div>
                      );
                    }

                    setAnalysisHoverToast({
                      status,
                      message,
                      kind: 'REVIEW_COUNT'
                    });
                  }}
                  onMouseLeave={() => setAnalysisHoverToast(null)}
                  className="w-8 h-8 flex items-center justify-center cursor-pointer transition focus:outline-none"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" height="22px" viewBox="0 -960 960 960" width="22px" fill={getStatusFill(reviewCount >= 5 ? 'GREEN' : reviewCount >= 1 ? 'YELLOW' : 'RED')}><path d="m363-390 117-71 117 71-31-133 104-90-137-11-53-126-53 126-137 11 104 90-31 133ZM80-80v-720q0-33 23.5-56.5T160-880h640q33 0 56.5 23.5T880-800v480q0 33-23.5 56.5T800-240H240L80-80Zm126-240h594v-480H160v525l46-45Zm-46 0v-480 480Z"/></svg>
                </button>


                {/* 5) Scores */}
                {(() => {
                  const _vAvg = Number.isFinite(Number(viewerAvgRating)) ? Number(viewerAvgRating) : null;
                  const _cAvg = Number.isFinite(Number(safeAvg)) ? Number(safeAvg) : null;
                  const _vBand = getScoreBand(_vAvg);
                  const _cBand = getScoreBand(_cAvg);
                  const ssBand = (_vBand === 'RED' || _cBand === 'RED') ? 'RED'
                    : (_vBand === 'YELLOW' || _cBand === 'YELLOW') ? 'YELLOW'
                      : (_vBand === 'GREEN' && _cBand === 'GREEN') ? 'GREEN' : 'GRAY';
                  return (
                <button
                  type="button"
                  onClick={(e) => e.preventDefault()}
                  onMouseEnter={() => {
                    const viewerName = (viewerDisplayName || 'Viewer').trim();
                    const candidateName = (displayName || 'Candidate').trim();

                    const viewerFirst = viewerName.split(' ')[0];
                    const candidateFirst = candidateName.split(' ')[0];

                    const vAvg =
                      Number.isFinite(Number(viewerAvgRating)) ? Number(viewerAvgRating) : null;
                    const vCount =
                      Number.isFinite(Number(viewerReviewCount)) ? Number(viewerReviewCount) : null;

                    const cAvg =
                      Number.isFinite(Number(safeAvg)) ? Number(safeAvg) : null;
                    const cCount =
                      Number.isFinite(Number(safeCount)) ? Number(safeCount) : null;

                    const vBand = getScoreBand(vAvg);
                    const cBand = getScoreBand(cAvg);
                    const lowestBand =
                      (vBand === 'RED' || cBand === 'RED') ? 'RED'
                        : (vBand === 'YELLOW' || cBand === 'YELLOW') ? 'YELLOW'
                          : (vBand === 'GREEN' && cBand === 'GREEN') ? 'GREEN'
                            : 'GRAY';

                    const getTextColor = (val) => {
                      if (val === null || val === undefined) return 'text-gray-500';
                      const n = Number(val);
                      if (!Number.isFinite(n) || n <= 0) return 'text-red-600';
                      if (n >= 4.0) return 'text-green-600';
                      if (n >= 3.0) return 'text-yellow-600';
                      return 'text-red-600';
                    };

                    const topLine = (
                      <div className="flex items-center gap-[20px] font-semibold">
                        <div className={getTextColor(vAvg)}>
                          {viewerFirst} — {vAvg ? vAvg.toFixed(1) : '—'} ★ ({vCount ?? '—'} reviews)
                        </div>

                        <div className={getTextColor(cAvg)}>
                          {candidateFirst} — {cAvg ? cAvg.toFixed(1) : '—'} ★ ({cCount ?? '—'} reviews)
                        </div>
                      </div>
                    );

                    const coaching = (
                      <div className="space-y-3">
                        {topLine}

                        <div>
                          In general, many strong local businesses cluster in the mid-4 star range.
                          A perfect 5.0 can still be great — but when it's based on only a handful of
                          reviews, some consumers read it as "not enough data yet". A slightly lower score
                          with lots of reviews often feels more realistic than a perfect score with very few.
                        </div>
                      </div>
                    );

                    setAnalysisHoverToast({
                      status: lowestBand,
                      message: coaching,
                      kind: 'SCORES'
                    });
                  }}
                  onMouseLeave={() => setAnalysisHoverToast(null)}
                  className="w-8 h-8 flex items-center justify-center cursor-pointer transition focus:outline-none"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" height="22px" viewBox="0 -960 960 960" width="22px" fill={getStatusFill(ssBand)}><path d="M240-400h122l200-200q9-9 13.5-20.5T580-643q0-11-5-21.5T562-684l-36-38q-9-9-20-13.5t-23-4.5q-11 0-22.5 4.5T440-722L240-522v122Zm280-243-37-37 37 37ZM300-460v-38l101-101 20 18 18 20-101 101h-38Zm121-121 18 20-38-38 20 18Zm26 181h273v-80H527l-80 80ZM80-80v-720q0-33 23.5-56.5T160-880h640q33 0 56.5 23.5T880-800v480q0 33-23.5 56.5T800-240H240L80-80Zm126-240h594v-480H160v525l46-45Zm-46 0v-480 480Z"/></svg>
                </button>
                  );
                })()}




                {/* 6) Customers */}
                {(() => {
                  const _toCount = (n) => { const x = Number(n); return Number.isFinite(x) ? x : 0; };
                  const _vCust = _toCount(viewerCustomerCount);
                  const _cCustFR = candidateCustomerCount !== null && candidateCustomerCount !== undefined && Number.isFinite(Number(candidateCustomerCount))
                    ? _toCount(candidateCustomerCount)
                    : _toCount(candidate?.u_customer_count ?? candidate?.customer_count ?? candidate?.customers_count ?? candidate?.u_customers_count ?? candidate?.profile?.u_customer_count ?? candidate?.user?.u_customer_count ?? null);
                  const _custBand = (n) => { const x = Number(n); if (!Number.isFinite(x)) return 'RED'; if (x >= 5) return 'GREEN'; if (x >= 1) return 'YELLOW'; return 'RED'; };
                  const ccBand = (_custBand(_vCust) === 'RED' || _custBand(_cCustFR) === 'RED') ? 'RED' : (_custBand(_vCust) === 'YELLOW' && _custBand(_cCustFR) === 'YELLOW') ? 'YELLOW' : 'GREEN';
                  return (
                <button
                  type="button"
                  onClick={(e) => e.preventDefault()}
                  onMouseEnter={() => {
                    const viewerName = (viewerDisplayName || 'Viewer').trim();
                    const candidateName = (displayName || 'Candidate').trim();

                    const viewerFirst = viewerName.split(' ')[0];
                    const candidateFirst = candidateName.split(' ')[0];

                    const vCust =
                      (viewerCustomerCount === null || viewerCustomerCount === undefined)
                        ? null
                        : (Number.isFinite(Number(viewerCustomerCount)) ? Number(viewerCustomerCount) : null);

                    // Candidate customer count (REAL): use fetched count if available, otherwise fallback
                    const cCustFallbackRaw =
                      candidate?.u_customer_count ??
                      candidate?.customer_count ??
                      candidate?.customers_count ??
                      candidate?.u_customers_count ??
                      candidate?.profile?.u_customer_count ??
                      candidate?.user?.u_customer_count ??
                      null;

                      const hasCandidateFetchedCount =
                        candidateCustomerCount !== null &&
                        candidateCustomerCount !== undefined &&
                        Number.isFinite(Number(candidateCustomerCount));

                      const cCust =
                        hasCandidateFetchedCount
                          ? Number(candidateCustomerCount)
                          : (Number.isFinite(Number(cCustFallbackRaw)) ? Number(cCustFallbackRaw) : null);



const toCount = (n) => {
                      const x = Number(n);
                      return Number.isFinite(x) ? x : 0; // missing behaves like 0 for banding
                    };

                    const vCount = toCount(vCust);
                    const cCount = toCount(cCust);

                    const getCustomerBand = (n) => {
                      const x = Number(n);
                      if (!Number.isFinite(x)) return 'RED';
                      if (x >= 5) return 'GREEN';
                      if (x >= 1) return 'YELLOW';
                      return 'RED';
                    };

                    const vBand = getCustomerBand(vCount);
                    const cBand = getCustomerBand(cCount);

                    // Button/toast border uses the LOWEST user band:
                    // RED if any 0, YELLOW only if both are 1–4, GREEN if both >0 and at least one is >=5
                    const lowestBand =
                      (vBand === 'RED' || cBand === 'RED') ? 'RED'
                        : (vBand === 'YELLOW' && cBand === 'YELLOW') ? 'YELLOW'
                          : 'GREEN';

                    const getTextColor = (val) => {
                      const x = Number(val);
                      if (!Number.isFinite(x) || x <= 0) return 'text-red-600';
                      if (x >= 5) return 'text-green-600';
                      return 'text-yellow-600'; // 1–4
                    };

                    const topLine = (
                      <div className="flex items-center gap-[20px] font-semibold">
                        <div className={getTextColor(vCount)}>
                          {viewerFirst} — {vCount} customers
                        </div>

                        <div className={getTextColor(cCount)}>
                          {candidateFirst} — {cCount} customers
                        </div>
                      </div>
                    );

                    const paragraphBothHaveCustomers =
                      `${viewerFirst} has ${vCount} customers that they can introduce ${candidateFirst} to. ` +
                      `${candidateFirst} has ${cCount} customers that they can introduce ${viewerFirst} to. ` +
                      `Introducing your teammates is easy and simple in the eHandshake Sharing Center. ` +
                      `You can also send "invitation only" surveys to your customers to increase your review and score count.`;

                    const paragraphViewerZero =
                      `${viewerFirst} has 0 customers to introduce ${candidateFirst} to. ` +
                      `${candidateFirst} has ${cCount} customers that they can introduce ${viewerFirst} to. ` +
                      `Introducing your teammates is easy and simple in the eHandshake Sharing Center. ` +
                      `You can also send "invitation only" surveys to your customers to increase your review and score count.`;

                    const paragraphCandidateZero =
                      `${candidateFirst} has 0 customers to introduce ${viewerFirst} to. ` +
                      `${viewerFirst} has ${vCount} customers that they can introduce ${candidateFirst} to. ` +
                      `Adding more customers to the eHandshake system is simple and quick. ` +
                      `You can also send "invitation only" surveys to your customers to increase your review and score count.`;

                    const paragraph =
                      (vCount <= 0 && cCount <= 0)
                        ? paragraphViewerZero // both zero; keep it simple (still correct)
                        : (vCount <= 0)
                          ? paragraphViewerZero
                          : (cCount <= 0)
                            ? paragraphCandidateZero
                            : paragraphBothHaveCustomers;

                    const coaching = (
                      <div className="space-y-3">
                        {topLine}
                        <div>{paragraph}</div>
                      </div>
                    );

                    setAnalysisHoverToast({
                      status: lowestBand,
                      message: coaching,
                      kind: 'CUSTOMERS'
                    });

                  }}
                  onMouseLeave={() => setAnalysisHoverToast(null)}
                  className="w-8 h-8 flex items-center justify-center cursor-pointer transition focus:outline-none"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" height="22px" viewBox="0 -960 960 960" width="22px" fill={getStatusFill(ccBand)}><path d="m520-260 140-140q11-11 17.5-26t6.5-32q0-34-24-58t-58-24q-19 0-37.5 11T520-492q-30-28-47-38t-35-10q-34 0-58 24t-24 58q0 17 6.5 32t17.5 26l140 140Zm336-130L570-104q-12 12-27 18t-30 6q-15 0-30-6t-27-18L103-457q-11-11-17-25.5T80-513v-287q0-33 23.5-56.5T160-880h287q16 0 31 6.5t26 17.5l352 353q12 12 17.5 27t5.5 30q0 15-5.5 29.5T856-390ZM513-160l286-286-353-354H160v286l353 354ZM260-640q25 0 42.5-17.5T320-700q0-25-17.5-42.5T260-760q-25 0-42.5 17.5T200-700q0 25 17.5 42.5T260-640Zm220 160Z"/></svg>
                </button>
                  );
                })()}


                {/* 7) Social Connections */}
                {(() => {
                  const platforms = candidateSocialPlatforms || [];
                  const connCount = platforms.length;
                  const scBand = connCount > 0 ? 'GREEN' : 'GRAY';
                  const candidateFirst = ((displayName || 'Candidate').split(' ')[0] || 'Candidate').trim();
                  const hasPlatform = (p) => platforms.includes(p);

                  const platformIcons = (
                    <div className="space-y-2">
                      <div className="flex items-center justify-center gap-3">
                        {/* Facebook */}
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill={hasPlatform('facebook') ? '#1877F2' : '#9ca3af'}>
                          <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/>
                        </svg>
                        {/* X / Twitter */}
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill={hasPlatform('twitter') ? '#000000' : '#9ca3af'}>
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                        </svg>
                        {/* LinkedIn */}
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill={hasPlatform('linkedin') ? '#0A66C2' : '#9ca3af'}>
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                        </svg>
                        {/* TikTok */}
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill={hasPlatform('tiktok') ? '#000000' : '#9ca3af'}>
                          <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                        </svg>
                        {/* Instagram */}
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill={hasPlatform('instagram') ? '#E4405F' : '#9ca3af'}>
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                        </svg>
                      </div>
                      <div className="text-center text-[10px] text-gray-500">
                        {connCount === 0
                          ? candidateFirst + ' has not connected any social platforms'
                          : candidateFirst + ' has connected ' + connCount + ' of 5 social platforms'}
                      </div>
                    </div>
                  );

                  return (
                    <button
                      type="button"
                      onClick={(e) => e.preventDefault()}
                      onMouseEnter={() =>
                        setAnalysisHoverToast({
                          status: scBand,
                          message: platformIcons,
                          kind: 'SOCIAL'
                        })
                      }
                      onMouseLeave={() => setAnalysisHoverToast(null)}
                      className="w-8 h-8 flex items-center justify-center cursor-pointer transition focus:outline-none"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" height="22px" viewBox="0 -960 960 960" width="22px" fill={getStatusFill(scBand)}><path d="M600-120v-120H440v-400h-80v120H80v-320h280v120h240v-120h280v320H600v-120h-80v320h80v-120h280v320H600ZM160-760v160-160Zm520 400v160-160Zm0-400v160-160Zm0 160h120v-160H680v160Zm0 400h120v-160H680v160ZM160-600h120v-160H160v160Z"/></svg>
                    </button>
                  );
                })()}


{/* 8) Profile Completeness */}
{(() => {
  const _hasPP = !!(candidate?.u_profile_picture && String(candidate.u_profile_picture).trim());
  const _hasLogo = !!(candidate?.b_logo && String(candidate.b_logo).trim());
  const _zip = String(candidate?.b_zip_code ?? candidate?.b_zip ?? '').trim();
  const _addr = String(candidate?.b_address ?? '').trim();
  const _city = String(candidate?.b_city ?? '').trim();
  const _st = String(candidate?.b_state ?? '').trim();
  const _addrLine = [_addr, [_city, _st, _zip].filter(Boolean).join(' ')].filter(Boolean).join(', ').trim();
  const _hasAddr = !!(_addrLine || _zip);
  const _hasPhone = !!(phoneText && String(phoneText).trim());
  const _aboutRaw = String(candidate?.b_about_us ?? '').trim();
  const _aboutWords = _aboutRaw.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().split(/\s+/).filter(Boolean).length;
  const _mm = (_hasPP ? 0 : 1) + (_hasLogo ? 0 : 1) + (_hasAddr ? 0 : 1) + (_hasPhone ? 0 : 1);
  const pcBand = (_mm >= 2 || _aboutWords === 0) ? 'RED' : (_mm === 1 || _aboutWords < 30) ? 'YELLOW' : 'GREEN';
  return (
<button
  type="button"
  onClick={(e) => e.preventDefault()}
  onMouseEnter={() => {
    const candidateFirst =
      (candidate?.u_first_name ? String(candidate.u_first_name).trim() : '') ||
      (candidate?.first_name ? String(candidate.first_name).trim() : '') ||
      'Candidate';

    const hasProfilePic = !!(candidate?.u_profile_picture && String(candidate.u_profile_picture).trim());

    const logoSrc = (candidate?.b_logo && String(candidate.b_logo).trim()) ? String(candidate.b_logo).trim() : '';
    const hasLogo = !!logoSrc;

    const zip = String(candidate?.b_zip_code ?? candidate?.b_zip ?? '').trim();
    const address = String(candidate?.b_address ?? '').trim();
    const city = String(candidate?.b_city ?? '').trim();
    const state = String(candidate?.b_state ?? '').trim();

    const addrLine = (() => {
      const parts = [];
      if (address) parts.push(address);
      const cityStateZip = [city, state, zip].filter(Boolean).join(' ');
      if (cityStateZip) parts.push(cityStateZip);
      return parts.join(', ').trim();
    })();

    const hasAddressOrZip = !!(addrLine || zip);
    const hasPhone = !!(phoneText && String(phoneText).trim());

    const aboutRaw = String(candidate?.b_about_us ?? '').trim();
    const aboutText = aboutRaw.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    const aboutWords = aboutText ? aboutText.split(/\s+/).filter(Boolean).length : 0;

    const missingMajor =
      (hasProfilePic ? 0 : 1) +
      (hasLogo ? 0 : 1) +
      (hasAddressOrZip ? 0 : 1) +
      (hasPhone ? 0 : 1);

    const status =
      (missingMajor >= 2 || aboutWords === 0)
        ? 'RED'
        : (missingMajor === 1 || aboutWords < 30)
          ? 'YELLOW'
          : 'GREEN';

    setAnalysisHoverToast({
      status,
      kind: 'PROFILE_CHECK',
      message: (
        <div className="space-y-1">
          <div className="text-xs font-bold text-[#00aef7]">{candidateFirst} — Profile Check</div>

          <div className="flex items-center justify-between gap-3">
            <div>
              Profile photo:{' '}
              <span className={hasProfilePic ? 'text-green-600' : 'text-red-600'}>
                {hasProfilePic ? 'Yes' : 'Missing'}
              </span>
            </div>
            <img
              src={profileImg}
              alt="Profile"
              className="w-10 h-10 rounded-full object-cover border-2 border-gray-200 shadow-sm"
            />
          </div>

          <div className="flex items-center justify-between gap-3">
            <div>
              Company logo:{' '}
              <span className={hasLogo ? 'text-green-600' : 'text-red-600'}>
                {hasLogo ? 'Yes' : 'Missing'}
              </span>
            </div>
            {hasLogo ? (
              <img
                src={logoSrc}
                alt="Logo"
                className="w-10 h-10 rounded object-contain border-2 border-gray-200 bg-white shadow-sm"
              />
            ) : (
              <div className="w-10 h-10 rounded border-2 border-gray-200 bg-gray-100" />
            )}
          </div>

          <div>
            Address:{' '}
            <span className={hasAddressOrZip ? 'text-gray-700' : 'text-red-600'}>
              {addrLine || (zip ? `Zip ${zip}` : 'Missing')}
            </span>
          </div>

          <div>
            Phone:{' '}
            <span className={hasPhone ? 'text-gray-700' : 'text-red-600'}>
              {hasPhone ? phoneText : 'Missing'}
            </span>
          </div>

          <div>
            About Us:{' '}
            <span className={aboutWords >= 30 ? 'text-green-600' : aboutWords > 0 ? 'text-yellow-600' : 'text-red-600'}>
              {aboutWords} words
            </span>
          </div>
        </div>
      )
    });
  }}
  onMouseLeave={() => setAnalysisHoverToast(null)}
  className="w-8 h-8 flex items-center justify-center cursor-pointer transition focus:outline-none"
>
  <svg xmlns="http://www.w3.org/2000/svg" height="22px" viewBox="0 -960 960 960" width="22px" fill={getStatusFill(pcBand)}><path d="m424-312 282-282-56-56-226 226-114-114-56 56 170 170ZM200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h560q33 0 56.5 23.5T840-760v560q0 33-23.5 56.5T760-120H200Zm0-80h560v-560H200v560Zm0-560v560-560Z"/></svg>
</button>
  );
})()}



              </div>
            </div>
          )}





          {/* Reviews section: ONLY this area scrolls */}
          <div className="flex-1 min-h-0 flex flex-col">



            <div
              className="flex-1 min-h-0 overflow-y-auto overscroll-contain scrollbar-thin scrollbar-thumb-gray-100 scrollbar-track-transparent pr-2 pb-6"
              onScroll={onReviewsScroll}
            >

              {isLoadingReviews ? (
                <div className="text-xs text-gray-500 text-center py-4">
                  Loading reviews…
                </div>
              ) : (reviews.length === 0 ? (
                <div className="text-xs text-gray-500 text-center py-4">
                  No reviews yet
                </div>
              ) : (
                <AllReviewsList
                  reviews={reviews.slice(0, visibleCount)}
                  showReplyStatus={false}
                  hideControls={true}
                />
              ))}
            </div>
          </div>

          </div>

        </div>
      </Rnd>

    </>
  );
}

// Small, stacked modal shown over the candidate card to pick one/many teams.
function TeamPickModal({ teams = [], onCancel, onConfirm }) {
  const [checked, setChecked] = useState(() => new Set());

  const toggle = (id) => {
    const s = new Set(checked);
    if (s.has(id)) s.delete(id); else s.add(id);
    setChecked(s);
  };

  return (
    <div className="fixed inset-0 z-[13001] flex items-center justify-center">
      {/* light scrim that doesn't close on click (so user must choose) */}
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative w-[360px] max-w-[90vw] rounded-xl bg-white shadow-2xl border p-4">
        <div className="text-base font-semibold mb-2">Add to which team(s)?</div>
        <div className="text-xs text-gray-600 mb-3">
          Select one or more teams and click <span className="font-medium">Add</span>. Additional team comparisons will be available in the Recruiting List.
        </div>

        <div className="space-y-2 mb-4">
          {teams.map(t => (
            <label key={t.team_id} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={checked.has(Number(t.team_id))}
                onChange={() => toggle(Number(t.team_id))}
              />
              <span className="truncate">
                {t.team_name} <span className="text-gray-500 text-xs">({t.role})</span>
              </span>
            </label>
          ))}
        </div>

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm rounded-md border hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(Array.from(checked))}
            disabled={checked.size === 0}
            className={`px-3 py-1.5 text-sm rounded-md ${
              checked.size === 0
                ? 'bg-[#00aef7]/40 text-white cursor-not-allowed'
                : 'bg-[#00aef7] text-white hover:bg-[#0099da]'
            }`}
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );
}

function AddToRecruitButton({ candidate, viewerUId = null, inline = false, variant = 'text' }) {
  const [status, setStatus] = useState('idle'); // 'idle' | 'adding' | 'added' | 'error'
  const [errMsg, setErrMsg] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [myTeams, setMyTeams] = useState([]);
  const { recruitingOpen } = useModalStore();
  const { teamId, addCandidates } = useRecruitingStore(); // you already import useRecruitingStore; this just also reads teamId

  // IMPORTANT: ensure this matches the actually signed-in user
  const viewerId =
    Number(viewerUId) ||
    (Number(window.localStorage.getItem('u_id')) || null);

  const candidateId = Number(candidate?.u_id ?? candidate?.id ?? NaN);
  const canResolveCandidate = Number.isFinite(candidateId);

  const fetchMyTeams = async () => {
    if (!viewerId) throw new Error('Missing viewer id (u_id).');
    const res = await fetch(`/api/teams/recruiting/debug/my-teams?u_id=${viewerId}`, { credentials: 'include' });
    if (!res.ok) throw new Error(`GET my-teams ${res.status}`);
    const json = await res.json();
    return Array.isArray(json?.teams) ? json.teams : [];
  };

  const postAdd = async (teamId) => {
    const res = await fetch('/api/teams/recruiting/items', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        team_id: Number(teamId),
        added_by_u_id: Number(viewerId),
        candidate_u_ids: [candidateId],
      }),
    });
    if (!res.ok) throw new Error(`POST /items ${res.status}`);
  };

  const onAddClick = async () => {
    setErrMsg('');
    if (!canResolveCandidate) { setErrMsg('Cannot determine candidate user id.'); return; }
    if (!viewerId) { setErrMsg('Missing viewer id (u_id).'); return; }

    try {
      // If Recruiting Modal is open on a specific team, add directly to that team (skip picker)
      if (recruitingOpen && Number.isFinite(Number(teamId))) {
        try {
          setStatus('adding');

          // 1) Backend add to the active Recruiting Modal's team
          await postAdd(Number(teamId));

          // 2) Seed global cache so avatar/name render instantly in RecruitingModal
          try { useOtherUsersStore.getState().upsertUsers([candidate]); } catch {}

          // 3) Reflect immediately in Recruiting store (adds to open shortlist)
          await addCandidates([candidateId]);

          setStatus('added');
          setTimeout(() => setStatus('idle'), 1200);
        } catch (e) {
          console.error('[AddToRecruitButton] direct-add failed', e);
          setStatus('error');
          setErrMsg('Failed to add candidate.');
        }
        return; // important: stop here (do not open team picker)
      }

      const teams = await fetchMyTeams();

      // 0 teams: informative message
      if (teams.length === 0) {
        setErrMsg('You are not on any teams yet.');
        return;
      }

      // 1 team: add immediately, no modal
      if (teams.length === 1) {
        setStatus('adding');

        // 1) Add on the backend
        await postAdd(teams[0].team_id);

        // 2) Seed the global cache so avatar/name render instantly in the Recruiting Modal
        try {
          useOtherUsersStore.getState().upsertUsers([candidate]);
        } catch {}

        // 3) Reflect immediately in the Recruiting store (uses current teamId in that store)
        await addCandidates([candidateId]);

        setStatus('added');
        setTimeout(() => setStatus('idle'), 1200);
        return;
      }



      // 2–3 teams (or more): open picker modal
      setMyTeams(teams);
      setPickerOpen(true);
    } catch (e) {
      console.error('[AddToRecruitButton] load teams failed', e);
      setErrMsg('Unable to load your teams.');
    }
  };

  const onConfirmTeams = async (teamIds) => {
    try {
      setStatus('adding');
      // 1) Backend adds
      for (const tid of teamIds) {
        await postAdd(tid);
      }
      // 2) Seed global cache so name/photo render instantly
      try { useOtherUsersStore.getState().upsertUsers([candidate]); } catch {}
      // 3) Reflect immediately in Recruiting store (current team in that store)
      await addCandidates([candidateId]);

      setStatus('added');
      setPickerOpen(false);
      setTimeout(() => setStatus('idle'), 1200);
    } catch (e) {
      console.error('[AddToRecruitButton] add failed', e);
      setStatus('error');
      setErrMsg('Failed to add candidate.');
    }
  };


  const disabled =
    status === 'adding' || !canResolveCandidate || !viewerId;

  const isIcon = variant === 'icon';

  const iconTitle =
    status === 'adding'
      ? 'Adding…'
      : status === 'added'
      ? 'Added!'
      : 'Add to Recruiting List';

  return (
    <div
      className={
        isIcon
          ? "w-full flex items-center justify-center relative"
          : (inline
              ? "w-full flex items-center justify-center gap-2 relative"
              : "mt-2 flex flex-col items-center gap-2 relative")
      }
    >
      <button
        type="button"
        onClick={onAddClick}
        disabled={disabled}
        className={
          isIcon
            ? [
                "h-10 w-10 inline-flex items-center justify-center rounded-md",
                "focus:outline-none focus:ring-2 focus:ring-[#00aef7]/30",
                disabled
                      ? "text-gray-300 cursor-not-allowed"
                      : "text-[#00aef7] hover:bg-[#00aef7]/10"
              ].join(" ")
            : `w-full inline-flex items-center justify-center gap-2 rounded-md border border-[#00aef7] px-3 py-1.5 text-xs font-semibold text-[#00aef7] hover:bg-[#00aef7]/5 disabled:opacity-50 bg-white ${inline ? '' : ''}`
        }
        title={iconTitle}
        aria-label={iconTitle}
      >
{isIcon ? (
  <>
    <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#00aef7"><path d="M720-400v-120H600v-80h120v-120h80v120h120v80H800v120h-80ZM247-527q-47-47-47-113t47-113q47-47 113-47t113 47q47 47 47 113t-47 113q-47 47-113 47t-113-47ZM40-160v-112q0-34 17.5-62.5T104-378q62-31 126-46.5T360-440q66 0 130 15.5T616-378q29 15 46.5 43.5T680-272v112H40Zm80-80h480v-32q0-11-5.5-20T580-306q-54-27-109-40.5T360-360q-56 0-111 13.5T140-306q-9 5-14.5 14t-5.5 20v32Zm296.5-343.5Q440-607 440-640t-23.5-56.5Q393-720 360-720t-56.5 23.5Q280-673 280-640t23.5 56.5Q327-560 360-560t56.5-23.5ZM360-640Zm0 400Z"/></svg>
    <span className="sr-only">{iconTitle}</span>
  </>
) : (

          <>
            {status === 'adding'
              ? 'Adding…'
              : status === 'added'
              ? 'Added!'
              : 'Add to Recruiting List'}
          </>
        )}
      </button>

      {!!errMsg && !isIcon && <div className="text-xs text-red-600">{errMsg}</div>}

      {pickerOpen && (
        <TeamPickModal
          teams={myTeams}
          onCancel={() => setPickerOpen(false)}
          onConfirm={onConfirmTeams}
        />
      )}
    </div>
  );
}


